import { useCallback, useState, type ReactNode } from 'react';
import { Modal } from '@/shared/components/Modal';
import { track } from '@/lib/analytics';
import type { ProfileDraft } from '../../domain/entities/auth.entity';
import { CompleteProfileForm } from './CompleteProfileForm';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { SignInForm } from './SignInForm';
import { SignUpForm } from './SignUpForm';

/**
 * Por que a pessoa está aqui. Decide só o texto do topo — é o que faz quem
 * terminou o ensaio ler "Ensaio concluído!" em vez de um convite genérico.
 */
export type AuthReason = 'ensaio' | 'gabarito' | 'login';

/**
 * O passo que o diálogo está mostrando. Cadastro e login não têm rota, como no
 * `enamed-feedback-lp`: vivem aqui, por cima da tela de onde a pessoa veio.
 */
export type AuthView =
  /** "Você já tem conta?" — a bifurcação de quem acabou de preencher o gabarito. */
  | { kind: 'ask' }
  | {
      kind: 'sign-in';
      email?: string;
      notice?: string;
      /** O que o cadastro já tinha coletado, quando o login veio do 409. */
      draft?: ProfileDraft;
    }
  | { kind: 'sign-up' }
  | { kind: 'forgot-password'; email?: string }
  /**
   * A conta existe, mas não tem perfil nesta plataforma: o cadastro curto
   * (nome, telefone, especialidade). Com `draft`, ele se completa sozinho.
   */
  | { kind: 'complete-profile'; draft?: ProfileDraft };

interface Copy {
  title: string;
  note: ReactNode;
}

const COPY: Record<AuthReason, Record<AuthView['kind'], Copy>> = {
  ensaio: {
    ask: { title: 'Ensaio concluído!', note: 'Você já tem conta?' },
    'sign-up': {
      title: 'Ensaio concluído!',
      note: 'Crie sua conta para enviar o ensaio e receber o seu diagnóstico.',
    },
    'sign-in': {
      title: 'Entre para ver seu diagnóstico',
      note: 'Use o e-mail e a senha da sua conta.',
    },
    'forgot-password': {
      title: 'Esqueceu sua senha?',
      note: 'Informe o e-mail da sua conta e enviaremos um link para criar uma senha nova.',
    },
    'complete-profile': {
      title: 'Falta pouco',
      note: 'Sua conta já existe. Confirme seus dados para enviar o ensaio e receber o seu diagnóstico.',
    },
  },
  gabarito: {
    ask: {
      title: 'Gabarito preenchido!',
      note: 'Para enviar e ver sua correção, entre na sua conta. Você já tem conta?',
    },
    'sign-up': {
      title: 'Crie sua conta',
      note: 'É com ela que você envia o gabarito e acompanha a correção.',
    },
    'sign-in': {
      title: 'Entre para enviar seu gabarito',
      note: 'Use o e-mail e a senha da sua conta.',
    },
    'forgot-password': {
      title: 'Esqueceu sua senha?',
      note: 'Informe o e-mail da sua conta e enviaremos um link para criar uma senha nova.',
    },
    'complete-profile': {
      title: 'Falta pouco',
      note: 'Sua conta já existe. Confirme seus dados para enviar o gabarito e ver sua correção.',
    },
  },
  login: {
    ask: { title: 'Acesse sua conta', note: 'Você já tem conta?' },
    'sign-up': {
      title: 'Crie sua conta',
      note: 'Para fazer o ensaio, enviar seu gabarito e ver o ranking.',
    },
    'sign-in': {
      title: 'Acesse sua conta',
      note: 'Use o e-mail e a senha da sua conta.',
    },
    'forgot-password': {
      title: 'Esqueceu sua senha?',
      note: 'Informe o e-mail da sua conta e enviaremos um link para criar uma senha nova.',
    },
    'complete-profile': {
      title: 'Falta pouco',
      note: 'Sua conta já existe. Confirme seus dados para acessar o ensaio, o gabarito e o ranking.',
    },
  },
};

interface AuthModalProps {
  view: AuthView | null;
  reason: AuthReason;
  onChangeView: (view: AuthView) => void;
  onClose: () => void;
  /** Entrou ou criou a conta. Quem hospeda segue o fluxo (envia, navega). */
  onAuthenticated: () => void;
}

export function AuthModal({
  view,
  reason,
  onChangeView,
  onClose,
  onAuthenticated,
}: AuthModalProps) {
  if (!view) return null;
  const copy = COPY[reason][view.kind];

  return (
    <Modal open onClose={onClose} title={copy.title} note={copy.note}>
      {view.kind === 'ask' && (
        <div className="modal-actions">
          <button
            type="button"
            className="ghost ghost-lg"
            onClick={() => {
              track({ event: 'ja_tem_conta', resposta: 'nao' });
              onChangeView({ kind: 'sign-up' });
            }}
          >
            Não, criar conta
          </button>
          <button
            type="button"
            className="cta cta-inline cta-block"
            onClick={() => {
              track({ event: 'ja_tem_conta', resposta: 'sim' });
              onChangeView({ kind: 'sign-in' });
            }}
          >
            Sim, entrar
          </button>
        </div>
      )}

      {view.kind === 'sign-in' && (
        <>
          <SignInForm
            origem={reason}
            initialEmail={view.email}
            notice={view.notice}
            onSuccess={(user) => {
              // Conta de outra plataforma: entrou, mas ainda não pode enviar
              // nada aqui. O cadastro curto vem antes de seguir o fluxo.
              if (user.hasProfile) onAuthenticated();
              else
                onChangeView({ kind: 'complete-profile', draft: view.draft });
            }}
            onForgotPassword={(email) =>
              onChangeView({ kind: 'forgot-password', email })
            }
          />
          <p className="modal-switch">
            Ainda não tem conta?{' '}
            <button
              type="button"
              onClick={() => onChangeView({ kind: 'sign-up' })}
            >
              Criar conta
            </button>
          </p>
        </>
      )}

      {view.kind === 'sign-up' && (
        <>
          <SignUpForm
            origem={reason}
            onSuccess={onAuthenticated}
            onExistingAccount={(email, draft) =>
              onChangeView({
                kind: 'sign-in',
                email,
                draft,
                notice:
                  'Este e-mail já tem conta. Entre com a sua senha para continuar.',
              })
            }
          />
          <p className="modal-switch">
            Já tem conta?{' '}
            <button
              type="button"
              onClick={() => onChangeView({ kind: 'sign-in' })}
            >
              Entrar
            </button>
          </p>
        </>
      )}

      {view.kind === 'complete-profile' && (
        <CompleteProfileForm
          origem={reason}
          draft={view.draft}
          onSuccess={onAuthenticated}
        />
      )}

      {view.kind === 'forgot-password' && (
        <ForgotPasswordForm
          initialEmail={view.email}
          onBackToSignIn={() => onChangeView({ kind: 'sign-in' })}
        />
      )}
    </Modal>
  );
}

/**
 * O estado do diálogo, para quem o hospeda: `open` num passo e as props
 * prontas para o `<AuthModal />`. O que fazer depois de entrar é de quem abre —
 * enviar o ensaio, enviar o gabarito, ir ao painel.
 */
export function useAuthModal(reason: AuthReason, onAuthenticated: () => void) {
  const [view, setView] = useState<AuthView | null>(null);
  const close = useCallback(() => setView(null), []);

  // Abrir conta como evento: é o topo do funil de cadastro. As trocas de passo
  // dentro do diálogo não contam — senão um vai-e-volta infla o número.
  const open = useCallback(
    (next: AuthView) => {
      track({ event: 'auth_aberto', origem: reason, passo: next.kind });
      setView(next);
    },
    [reason],
  );

  return {
    open,
    close,
    modalProps: {
      view,
      reason,
      onChangeView: setView,
      onClose: close,
      onAuthenticated: () => {
        setView(null);
        onAuthenticated();
      },
    } satisfies AuthModalProps,
  };
}
